/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd'; // Drag-n-Drop framework
import { HTML5Backend } from 'react-dnd-html5-backend';
import Attributes from '../model/Attributes';
import NumberSlot from './NumberSlot';
import EditableText from '../EditableText';
import Race from "../model/Race";
import AttributeValues from "../model/AttributeValues";
import ASIOption from '../model/ASIOption';
import MarkdownText from '../MarkdownText';

interface AbilitiesPageProps {
    race: Race | null;
    attributeBases: number[];
    onAttributeBaseChanged: (newValues: number[]) => void;
    diceArray: string;
    setDiceArray: (value: string) => void;
    asiRacialSelected: string[];
    onAsiRacialSelectedChanged: (newValues: string[]) => void;
    asiClassSelected: string[];
    onAsiClassSelectedChanged: (newValues: string[]) => void;
    attributeValues: AttributeValues;
    onAttributeValuesChanged: (newValuesArray: number[], newValues: AttributeValues) => void;
    asiRacialOptions: ASIOption[];
    asiClassOptions: ASIOption[];
    featsByName: Map<string, Ability>;
}

const AbilitiesPage: React.FC<AbilitiesPageProps> = ({
    race,
    attributeBases,
    onAttributeBaseChanged,
    diceArray,
    setDiceArray,
    asiRacialSelected,
    onAsiRacialSelectedChanged,
    asiClassSelected,
    onAsiClassSelectedChanged,
    attributeValues,
    onAttributeValuesChanged,
    asiRacialOptions,
    asiClassOptions,
    featsByName
}) => {
    const [raceAdj, setRaceAdj] = useState<number[]>([0, 0, 0, 0, 0, 0]);
    const [asiAdj, setAsiAdj] = useState<number[]>([0, 0, 0, 0, 0, 0]);

    useEffect(() => {
        const raceAdjustment = Attributes.attributes.map((attribute, i) => {
            let racialBonus = 0;
            (race?.asi || []).forEach(asi => {
                if (asi.attributes[0].toLowerCase() === attribute.toLowerCase()) {
                    racialBonus += asi.value;
                }
            });
            return racialBonus;
        });
        setRaceAdj(raceAdjustment);
    }, [race]);

    useEffect(() => {
        const asiAdjustment = Attributes.attributes.map((attribute, i) => {
            const asiAdj = asiClassSelected.filter(asi => asi === attribute).length;
            return asiAdj;
        });

        const raceAdjustment = Attributes.attributes.map((attribute, i) => {
            const raceAdj = asiRacialSelected.reduce((sum, asi) => {
                if (asi.split(':')[0].toLowerCase() === attribute.toLowerCase()) {
                    return sum + (parseInt(asi.split(':')[1]) || 1);
                }
                return sum;
            }, 0);
            return raceAdj;
        });

        setAsiAdj(asiAdjustment);
        setRaceAdj(raceAdjustment);
        const attributes = attributeBases.map((base, i) => base + asiAdjustment[i] + raceAdjustment[i]);

        const changed = attributeValues.strength     !== attributes[Attributes.STR] ||
                        attributeValues.dexterity    !== attributes[Attributes.DEX] ||
                        attributeValues.constitution !== attributes[Attributes.CON] ||
                        attributeValues.intelligence !== attributes[Attributes.INT] ||
                        attributeValues.wisdom       !== attributes[Attributes.WIS] ||
                        attributeValues.charisma     !== attributes[Attributes.CHA];
        if (changed) {
          attributeValues.strength     = attributes[Attributes.STR];
          attributeValues.dexterity    = attributes[Attributes.DEX];
          attributeValues.constitution = attributes[Attributes.CON];
          attributeValues.intelligence = attributes[Attributes.INT];
          attributeValues.wisdom       = attributes[Attributes.WIS];
          attributeValues.charisma     = attributes[Attributes.CHA];
          onAttributeValuesChanged(attributeBases, attributeValues);
        }
    }, [asiClassSelected, asiRacialSelected, attributeBases, attributeValues, onAttributeValuesChanged]);

    const useRandomArray = () => {
        const randomValues = Array.from({ length: 6 }, () => {
            const dice = Array.from({ length: 4 }, () => Math.floor(Math.random() * 6) + 1).sort((a, b) => b - a);
            return dice[0] + dice[1] + dice[2];
        });
        onAttributeBaseChanged(randomValues);
        setDiceArray('random');
    };

    const useStandardArray = () => {
        onAttributeBaseChanged([15, 14, 13, 12, 10, 8]);
        setDiceArray('standard');
    };

    const useWeakArray = () => {
        onAttributeBaseChanged([13, 12, 11, 10, 9, 8]);
        setDiceArray('weak');
    };

    const useManualArray = () => {
        // leave the values the same from whatever they were before they clicked on manual:
        // onAttributeBaseChanged([10, 10, 10, 10, 10, 10]);
        setDiceArray('manual');
    };

    const moveNumber = (from: number, to: number) => {
        const updatedAttributeBases = [...attributeBases];
        [updatedAttributeBases[from], updatedAttributeBases[to]] = [updatedAttributeBases[to], updatedAttributeBases[from]];
        onAttributeBaseChanged(updatedAttributeBases);
    };

    const changeNumber = (index: number, newValue: number) => {
        const updatedAttributeBases = [...attributeBases];
        updatedAttributeBases[index] = newValue;
        onAttributeBaseChanged(updatedAttributeBases);
    };

    // Handle change in select box
    const handleASIChange = (attribute: string, level: number, index: number, racial: boolean) => {
        if (racial) {
            const updatedValues = [...asiRacialSelected];
            updatedValues[index] = attribute + ":" + level;
            onAsiRacialSelectedChanged(updatedValues);
        } else {
            const updatedValues = [...asiClassSelected];
            updatedValues[index] = attribute;
            onAsiClassSelectedChanged(updatedValues);
        }
    };

    const renderASI = (asi: ASIOption, index: number, racial: boolean) => {
        let attrOptions: string[] = [...Attributes.attributes];
        if (!racial && asi.attribute === 'AnyFeat' && asi.pairedASI) {
          attrOptions.push('Feat');
        }
        const attr = asi.attribute;
        if (racial && (attr !== 'Feat') && attr !== 'AnyFeat') {
            if (attr === 'Other') {
                for (let i = 0; i < index; i++) {
                    attrOptions = attrOptions.filter(o => o !== asiRacialSelected[i]?.split(":")[0]);
                }
            } else if (attr !== 'Any') {
                attrOptions = [attr];
                asiRacialSelected[index] = attr + ":" + asi.level;
            }
        } else if (attr === 'Feat') {
            for (let i = 0; i < index; i++) {
                attrOptions = attrOptions.filter(o => o !== asiClassSelected[i]);
            }
        }
        if (!racial && asiClassSelected[index-1] === 'Feat') {
          attrOptions = [...featsByName.keys()].sort();
        }
        const value = (racial ? asiRacialSelected[index]?.split(":")[0] : asiClassSelected[index]) || '';
        return (
            <div className='no-wrap' key={index}>
                {asi.label}:&nbsp;
                <select value={value}
                    style={{ width: '100px' }}
                    disabled={attrOptions.length === 1}
                    onChange={(e) => handleASIChange(e.target.value, asi.level, index, racial)}>
                    {value ? null : <option value=''>-- Select --</option>}
                    {
                        attrOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))
                    }
                </select> +{asi.level}
            </div>
        );
    };

    // Render select boxes based on the current number of specified boxes
    const renderASIs = () => {
        return (
            <>
                <hr />
                <div style={{ textAlign: 'center', width: '100%', fontWeight: '600', textDecoration: 'underline' }}>Racial ASIs</div>
                {asiRacialOptions.map((asi, index) => renderASI(asi, index, true))}
                <hr />
                <div style={{ textAlign: 'center', width: '100%', fontWeight: '600', textDecoration: 'underline' }}>Class ASIs</div>
                {asiClassOptions.map((asi, index) => renderASI(asi, index, false))}
            </>
        );
    };

    const renderMainBlock = () => {
        return (
          <table>
            <tbody>
              <tr>
                <td></td>
                {Attributes.attributes.map((attribute, i) => (
                  <th key={i}>{attribute.substring(0, 1).toUpperCase() + attribute.substring(1, 3)}</th>
                ))}
              </tr>
              <tr>
                <th style={{ textAlign: 'right' }}>
                  {diceArray === 'random' ? 'Dice Roll:' :
                    diceArray === 'standard' ? 'Standard Array:' :
                      diceArray === 'weak' ? 'Weak Array:' :
                        diceArray === 'manual' ? 'Assign:' : ''
                  }
                </th>
                {attributeBases.map((attributeBase, i) => (
                  <td key={i} className='attribute-selector'>
                    {diceArray !== 'manual' && <NumberSlot key={i} number={attributeBase} index={i} moveNumber={moveNumber} />}
                    {diceArray === 'manual' && (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '5px',
                        backgroundColor: 'lightblue',
                        border: '1px solid black'
                      }}>
                        <EditableText type="number" min={1} max={30} width='50px' initialValue={attributeBase}
                          onSave={(newValue) => changeNumber(i, (Math.max(0, parseInt('' + newValue))))} />
                      </div>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <th style={{ textAlign: 'right' }}>Racial&nbsp;Adj:</th>
                {raceAdj.map((adj, i) => (
                  <th key={i}> {adj ? `+${adj}` : ''}</th>
                ))}
              </tr>
              <tr>
                <th style={{ textAlign: 'right' }}>ASI Adj:</th>
                {asiAdj.map((adj, i) => (
                  <th key={i}> {adj ? `+${adj}` : ''}</th>
                ))}
              </tr>
              <tr>
                <th style={{ textAlign: 'right' }}>Results:</th>
                {Attributes.attributes.map((attribute, i) => (
                  <th className='attr-container' key={i}>
                    <span className='attr-name'>
                      {attribute.substring(0, 1).toUpperCase() + attribute.substring(1, 3)}
                    </span><br />
                    <span className='attr-num'>
                      {attributeBases[i] + raceAdj[i] + asiAdj[i]}
                    </span>
                  </th>
                ))}
              </tr>
            </tbody>
          </table>
        );
      };
    
      const renderFeats = (renderOddFeats: boolean) => {
        const feats = [];
        let isOdd: boolean = true;
        for (let index=0 ; index<asiClassSelected.length ; index++) {
          const feat = featsByName.get(asiClassSelected[index]);
          if (feat) {
            if (isOdd === renderOddFeats) {
              feats.push(
                <div key={index} style={{border: '1px solid black', borderRadius: '10px', margin: '5px', padding: '5px', backgroundColor: 'white'}}>
                  <p className="action-head">{feat.name}.</p>
                  <MarkdownText text={feat.desc} mon={null} />
                </div>
              );
            }
            isOdd = !isOdd;
          }
        }
        if (feats.length === 0) {
          return <></>;
        }
        return feats;
      }

      return (<>
        <button className={diceArray === 'random' ? 'dice-button selected' : 'dice-button'} onClick={useRandomArray}>Randomize</button>
        <button className={diceArray === 'standard' ? 'dice-button selected' : 'dice-button'} onClick={useStandardArray}>Standard</button>
        <button className={diceArray === 'weak' ? 'dice-button selected' : 'dice-button'} onClick={useWeakArray}>Weak</button>
        <button className={diceArray === 'manual' ? 'dice-button selected' : 'dice-button'} onClick={useManualArray}>Manual</button>
        <table>
          <tbody>
            <tr>
              <td>
                <DndProvider backend={HTML5Backend}>
                  {renderMainBlock()}
                </DndProvider>
              </td>
              <td style={{ width: '100%', textAlign: 'right' }}>{renderASIs()}</td>
            </tr>
            <tr>
              <td colSpan={2}><hr/>
                <h3>Feats:</h3>
                <table>
                  <tbody>
                    <tr>
                      <td style={{width: '50%', verticalAlign: 'top'}}>{renderFeats(true)}</td>
                      <td style={{width: '50%', verticalAlign: 'top'}}>{renderFeats(false)}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </>);
    };
    
    export default AbilitiesPage;
    